import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@clerk/clerk-react'
import { reconcileHarnessI18nConfig } from './lib/i18n/config'
import { getEngineUrl } from './lib/config'
import { aguardarPasseComPrazo, CREDENTIAL_WAIT_TIMEOUT_MS } from './lib/chat-stream'
import Router from './app/Router'
import AppToaster from './components/AppToaster'
import { useUIStore } from './stores/uiStore'
import { useAuthStore } from './shared/stores/auth'
import { useEngineConnection } from './lib/use-engine-connection'
import { ErrorBoundary } from './components/ErrorBoundary'

export default function App() {
  const { i18n } = useTranslation()
  const theme = useUIStore((s) => s.theme)
  const { isSignedIn, isLoaded, sessionId } = useAuth()

  // Etapa 2 da RAIZ 10 (STO-1334): busca o defaultLanguage do TENANT e aplica
  // — sem isto a "preferência do tenant" descrita em `locale-detection.ts`
  // nunca rodava, e o produto só via o default fixo do produto (pt-BR) ou o
  // idioma persistido do usuário. Fire-and-forget: nunca pode travar o boot.
  //
  // STO-1401 — autenticado pelo MESMO canal de identidade do resto do app
  // (`aguardarPasseComPrazo` + `useAuthStore().getToken()`, o padrão de
  // `chat-stream.ts`): `GET /i18n/config` está atrás de `requireAuth` no
  // motor, e sem o passe a chamada sempre leva 401. Só dispara depois que o
  // Clerk resolve (`isLoaded`) e só para sessão autenticada (`isSignedIn`) —
  // visitante deslogado não tem tenant, então não há preferência de tenant
  // para buscar. O passe tem PRAZO (não é laço de retry): estourou, segue sem
  // `Authorization` — o motor devolve a config GLOBAL e o boot nunca trava
  // esperando um token que não chega.
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return
    void (async () => {
      try {
        const passe = await aguardarPasseComPrazo(
          () => useAuthStore.getState().getToken(),
          CREDENTIAL_WAIT_TIMEOUT_MS,
        )
        const token = passe.kind === 'token' ? passe.token : null
        await reconcileHarnessI18nConfig(getEngineUrl(), fetch, localStorage, token)
      } catch {
        // getEngineUrl() pode lançar (env var ausente) — idioma é conforto,
        // nunca pode derrubar o boot do app.
      }
    })()
  }, [isLoaded, isSignedIn])

  // Apply theme on mount
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    if (theme === 'light') {
      document.documentElement.classList.add('light')
    } else {
      document.documentElement.classList.remove('light')
    }
  }, [theme])

  // Sync <html lang="..."> with the active i18next locale.
  // The HTML shell ships with lang="en" hardcoded; SEO + accessibility (screen
  // readers) + Harness V1 spec § 1 ("the html lang attribute is the canonical
  // signal") all require it to reflect the resolved locale post-detection.
  // Sprint B E2E (E3 pt-BR, E5 en-US override) asserts on this attribute.
  useEffect(() => {
    document.documentElement.lang = i18n.language
  }, [i18n.language])

  // RTL support
  useEffect(() => {
    const dir = ['ar', 'he', 'fa', 'ur'].some((l) => i18n.language.startsWith(l)) ? 'rtl' : 'ltr'
    document.documentElement.setAttribute('dir', dir)
  }, [i18n.language])

  useEngineConnection(isLoaded, isSignedIn, sessionId)

  return (
    <ErrorBoundary>
      <Router />
      <AppToaster />
    </ErrorBoundary>
  )
}
